/**
 * Reading a run's stored equity curve. Backlog task 70.
 *
 * The generated client cannot be used for this one endpoint, and the reason is worth stating
 * rather than discovering: `/backtests/{id}/equity-curve` returns **`application/json` with
 * `Content-Encoding: gzip`**, and the generator types it as `any` because the OpenAPI document
 * describes a raw `Response`. `fetch` decompresses it transparently — the same as any gzipped
 * response a server sends — so what arrives here is ordinary JSON.
 *
 * ## The equity values stay strings
 *
 * `money.ts` states the rule: *"a price stays a `string` from the wire to the pixel, except
 * inside the charting library"*. The worker writes them with `jmt_domain.money.plain`, so
 * `"0.000000000262"` never becomes `"2.62E-10"` and never becomes an IEEE double on the way to
 * the screen. `toPlot` is the one conversion, at the chart.
 */



export interface CurvePoint {
  /** ISO 8601, UTC — the instant the bar closed. */
  ts: string;
  /** Decimal as a string (H6). Never parsed except by `toPlot`. */
  equity: string;
}

/**
 * The curve for one run.
 *
 * Throws on anything other than 200 so TanStack Query reports it as an error rather than
 * rendering an empty chart — *"a failed request supports no statement about the data"* (F26).
 */
export async function fetchEquityCurve(runId: number): Promise<CurvePoint[]> {
  const points = await fetchCurve(runId, 'equity-curve');
  if (points === null) {
    // The endpoint 404s only when the run stored no curve, which for the strategy's own is a
    // defect in the worker rather than a state this screen renders.
    throw new Error(`run ${runId} stored no equity curve`);
  }
  return points;
}

/**
 * The passive control's curve for one run, or `null` when the run stored none.
 *
 * 🔴 **404 is `null`, not an error, and the distinction is the whole point of this function.**
 * Every run written before migration `0048` has no control curve, and a report must still render
 * for those. What it must not do is draw a flat line at zero — that is a specific and wrong claim
 * about the market rather than the absence of one — so the chart draws the strategy alone and the
 * note beneath it says why.
 */
export async function fetchBenchmarkCurve(runId: number): Promise<CurvePoint[] | null> {
  return fetchCurve(runId, 'benchmark-curve');
}

async function fetchCurve(
  runId: number,
  path: 'equity-curve' | 'benchmark-curve',
): Promise<CurvePoint[] | null> {
  // Same-origin, matching the generated client's `BASE: ''` — an absolute URL would make the
  // request cross-origin and the browser would withhold the `SameSite=Strict` session cookie
  // without saying so. The version prefix is the client's own (`API-Spec.md`: base `/api/v1`).
  const response = await fetch(`/api/v1/backtests/${runId}/${path}`, {
    credentials: 'same-origin',
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`${path} for run ${runId}: HTTP ${response.status}`);
  }
  const points: unknown = await response.json();
  if (!Array.isArray(points)) {
    throw new Error(`${path} for run ${runId} is not a list of points`);
  }
  return points as CurvePoint[];
}
